import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  Eye,
  FileText,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminInterventions = () => {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchInterventions();
  }, []);

  const fetchInterventions = async () => {
    try {
      const response = await axios.get(`${API}/interventions`);
      setInterventions(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des interventions');
    } finally {
      setLoading(false);
    }
  };

  // Admin validates/rejects on behalf of a slow SME so the consultant can submit their final report.
  const adminValidate = async (intervention, action) => {
    const verb = action === 'validate' ? 'valider' : 'rejeter';
    if (!window.confirm(`Voulez-vous ${verb} l'intervention de ${intervention.consultant_name} (${intervention.duration_hours}h) au nom de l'entreprise ?`)) return;
    let comment = '';
    if (action === 'reject') {
      comment = window.prompt('Motif du rejet (optionnel) :') || '';
    }
    try {
      await axios.post(`${API}/interventions/${intervention.id}/admin-validate`, { action, comment });
      toast.success(action === 'validate' ? 'Intervention validée' : 'Intervention rejetée');
      fetchInterventions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { styles: 'bg-orange-100 text-orange-700 border-orange-200', label: 'En attente' },
      validated: { styles: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: 'Validée' },
      rejected: { styles: 'bg-red-100 text-red-700 border-red-200', label: 'Rejetée' }
    };
    const { styles, label } = config[status];
    return <Badge variant="outline" className={styles}>{label}</Badge>;
  };

  const filteredInterventions = interventions.filter(intervention => {
    const matchesSearch = 
      intervention.sme_company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.consultant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      intervention.intervention_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || intervention.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="admin-interventions-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Toutes les interventions</h1>
          <p className="text-slate-500 mt-1">Consultez toutes les interventions de la plateforme</p>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par entreprise, consultant ou type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="validated">Validée</SelectItem>
                  <SelectItem value="rejected">Rejetée</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Interventions Table */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {filteredInterventions.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {interventions.length === 0 
                    ? 'Aucune intervention' 
                    : 'Aucune intervention ne correspond à vos filtres'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-slate-500 font-medium">Entreprise</TableHead>
                    <TableHead className="text-slate-500 font-medium">Consultant</TableHead>
                    <TableHead className="text-slate-500 font-medium">Type</TableHead>
                    <TableHead className="text-slate-500 font-medium">Date</TableHead>
                    <TableHead className="text-slate-500 font-medium">Durée</TableHead>
                    <TableHead className="text-slate-500 font-medium">Statut</TableHead>
                    <TableHead className="text-slate-500 font-medium text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInterventions.map((intervention) => (
                    <TableRow key={intervention.id} data-testid={`row-${intervention.id}`}>
                      <TableCell className="font-medium text-slate-900">
                        {intervention.sme_company_name}
                      </TableCell>
                      <TableCell className="text-slate-600">{intervention.consultant_name}</TableCell>
                      <TableCell className="text-slate-600">{intervention.intervention_type}</TableCell>
                      <TableCell className="text-slate-600">{intervention.date}</TableCell>
                      <TableCell className="text-slate-600">{intervention.duration_hours}h</TableCell>
                      <TableCell>{getStatusBadge(intervention.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {intervention.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                                onClick={() => adminValidate(intervention, 'validate')}
                                title="Valider au nom de l'entreprise"
                                data-testid={`admin-validate-${intervention.id}`}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                onClick={() => adminValidate(intervention, 'reject')}
                                title="Rejeter"
                                data-testid={`admin-reject-${intervention.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/interventions/${intervention.id}`)}
                            data-testid={`view-${intervention.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
